/**
 * `<RegenerateConfirmModal>` — generic confirm modal for the two
 * regenerate flows on the candidate-page tabs:
 *   - Interview Prep regenerate (interview-prep-spec.md §3.4)
 *   - Interview Analysis regenerate (interview-prep-spec.md §7.7)
 *
 * Phase 2.6.
 *
 * Default focus: Cancel (safe action) per global-patterns-spec.md §1.4.
 * Esc → cancel; click-outside-backdrop → cancel.
 *
 * Copy is consumer-supplied so the same component works for both regen
 * flows. The "replaces the existing" framing is owned by the parent
 * (the spec's per-flow copy differs subtly).
 */
import { useEffect, type JSX } from "react";
import { Button } from "@/components/common/Button";

interface RegenerateConfirmModalProps {
  title: string;
  body: string;
  confirmLabel?: string;
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function RegenerateConfirmModal({
  title,
  body,
  confirmLabel = "Regenerate",
  isPending,
  onConfirm,
  onCancel,
}: RegenerateConfirmModalProps): JSX.Element {
  useEffect(() => {
    const t = setTimeout(() => {
      const safeBtn = document.querySelector<HTMLButtonElement>(
        '[data-testid="regenerate-confirm-cancel"]',
      );
      safeBtn?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <div
      className="pointer-events-auto fixed inset-0 z-50 flex items-center justify-center bg-surface-overlay backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="regenerate-confirm-title"
      onKeyDown={handleKeyDown}
      onClick={onCancel}
      data-testid="regenerate-confirm-modal"
    >
      <div
        className="flex w-full max-w-md flex-col gap-4 rounded-lg border border-border-default bg-surface-elevated p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="regenerate-confirm-title"
          className="text-base font-semibold text-text-emphasis"
        >
          {title}
        </h2>
        <p className="text-sm text-text-secondary">{body}</p>
        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={isPending}
            data-testid="regenerate-confirm-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isPending}
            loading={isPending}
            data-testid="regenerate-confirm-confirm"
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
